import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VPSStatsCard from './VPSStatsCard';

/**
 * 管理者ポータルセクション
 * 管理者権限を持つユーザーのマイページに表示される管理機能へのリンク集
 */
const AdminPortalSection: React.FC = () => {
  const navigate = useNavigate();
  const [showStats, setShowStats] = useState(false);
  
  const adminFeatures = [
    {
      id: 'django-admin',
      title: 'Django 管理画面',
      description: 'ユーザー、プラン、データベース全体の管理',
      icon: 'fas fa-cogs',
      link: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/auth/admin/auto-login/`,
      color: 'primary',
      external: true,
      requiresAuth: true
    },
    {
      id: 'user-management',
      title: 'ユーザー管理',
      description: 'ユーザーアカウントとサブスクリプション情報の管理',
      icon: 'fas fa-users',
      link: '/admin/users',
      color: 'success',
      external: false
    },
    {
      id: 'subscription-management',
      title: 'サブスクリプション管理',
      description: 'プランの設定と利用状況の確認',
      icon: 'fas fa-credit-card',
      link: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/auth/subscription/auto-login/`,
      color: 'info',
      external: true,
      requiresAuth: true
    },
    {
      id: 'analytics',
      title: 'サイト統計',
      description: 'VPS使用状況とサービス利用統計',
      icon: 'fas fa-chart-bar',
      link: '#analytics',
      color: 'warning',
      external: false,
      onClick: () => setShowStats(!showStats)
    }
  ];

  const handleFeatureClick = async (feature: typeof adminFeatures[0]) => {
    if (feature.external) {
      if (feature.requiresAuth) {
        // 認証が必要な外部リンクの場合、hidden formを使用してトークンを送信
        const token = localStorage.getItem('authToken');
        if (process.env.NODE_ENV === 'development') {
          console.log('Retrieved token:', token ? '[TOKEN_HIDDEN]' : 'null');
        }
        if (token) {
          // シンプルなGETリクエストでテスト
          const cleanToken = token.replace(/"/g, '');
          const urlWithToken = `${feature.link}?token=${encodeURIComponent(cleanToken)}`;
          if (process.env.NODE_ENV === 'development') {
            console.log('Opening URL with token:', `${feature.link}?token=[TOKEN_HIDDEN]`);
          }
          window.open(urlWithToken, '_blank', 'noopener,noreferrer');
        } else {
          alert('認証が必要です。ログインしてください。');
        }
      } else {
        window.open(feature.link, '_blank', 'noopener,noreferrer');
      }
    } else {
      // 内部リンクの場合はReact Routerで遷移
      navigate(feature.link);
    }
  };

  return (
    <div className="admin-portal-section mb-5">
      {/* セクションヘッダー */}
      <div className="d-flex align-items-center mb-4">
        <div className="admin-portal-icon me-3">
          <i className="fas fa-shield-alt"></i>
        </div>
        <div>
          <h4 className="mb-1 fw-bold" style={{color: '#334155'}}>管理者ポータル</h4>
          <small className="text-muted">サイト管理機能へのクイックアクセス</small>
        </div>
      </div>

      {/* 管理機能カード */}
      <div className="row g-3">
        {adminFeatures.map((feature) => (
          <div key={feature.id} className="col-md-6 col-lg-3">
            <div 
              className={`admin-feature-card card h-100 border-0 shadow-sm border-${feature.color}`}
              onClick={() => handleFeatureClick(feature)}
              style={{ cursor: 'pointer' }}
            >
              <div className="card-body text-center p-3">
                <div className={`admin-feature-icon mb-2 text-${feature.color}`}>
                  <i className={`${feature.icon} fa-2x`}></i>
                </div>
                <h6 className="card-title mb-2">{feature.title}</h6>
                <p className="card-text small text-muted mb-2">
                  {feature.description}
                </p>
                <div className="admin-feature-action">
                  {feature.external ? (
                    <small className="text-primary">
                      <i className="fas fa-external-link-alt me-1"></i>
                      外部リンク
                    </small>
                  ) : (
                    <small className="text-secondary">
                      <i className="fas fa-arrow-right me-1"></i>
                      開く
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 管理者情報 */}
      <div className="admin-portal-info mt-4 p-3 bg-light rounded">
        <div className="row align-items-center">
          <div className="col-md-8">
            <div className="d-flex align-items-center">
              <i className="fas fa-info-circle me-2" style={{color: '#64748b'}}></i>
              <small className="text-muted">
                管理者権限でログインしています。すべての管理機能にアクセスできます。
              </small>
            </div>
          </div>
          <div className="col-md-4 text-md-end">
            <small className="fw-semibold" style={{color: '#475569'}}>
              <i className="fas fa-shield-alt me-1"></i>
              Administrator
            </small>
          </div>
        </div>
      </div>

      {/* VPS統計表示 */}
      {showStats && (
        <div className="mt-4">
          <VPSStatsCard />
        </div>
      )}
    </div>
  );
};

export default AdminPortalSection; 